import type { PostOfficeEnvironmentValues } from '@atlassian/post-office-context';
import {
	type PluginDimensions as AtlaskitPluginDimensions,
	type DependencyMap,
	ExternalModuleName,
	type ManifestMap,
	type ModuleUriMapper,
	PluginName,
	type PluginVariant,
	type RemoteModuleConfig,
	type RemotePlugin,
} from '@atlassian/remote-module-loader/types';
import memoizeOne from 'memoize-one';
import { version as REACT_VERSION } from 'react';

type AtlaskitPluginDimensionsSimplified = Omit<AtlaskitPluginDimensions, 'variant'>;

export interface PluginDimensions extends AtlaskitPluginDimensionsSimplified {
	readonly placementId: string;
}

const VARIANT_LATEST: PluginVariant = 'latest';

class RemotePostOfficePlugin implements RemotePlugin {
	public readonly name = PluginName.PostOffice;
	public readonly placementId: string;

	private readonly _dimensions: AtlaskitPluginDimensions;
	private readonly _manifestMap: ManifestMap;

	constructor(placementId: string, environmentContext: PostOfficeEnvironmentValues) {
		this.placementId = placementId;

		const { currentEnv, envConfigs } = environmentContext;

		const moduleName = `${MODULE_NAME}/${placementId}`;

		const mapPlacementEnvToRMLEnv = (placementEnv: PluginDimensions['env']) => {
			return {
				local: 'local',
				test: 'test',
				dev: 'local',
				staging: 'staging',
				production: 'production',
				'stg-fedm-east': 'staging',
				'prod-fedm-east': 'production',
			}[placementEnv] as PluginDimensions['env'];
		};

		this._dimensions = { env: mapPlacementEnvToRMLEnv(currentEnv), variant: VARIANT_LATEST };
		this._manifestMap = new Map([
			[
				'local',
				new Map([
					[
						VARIANT_LATEST,
						{
							moduleName,
							moduleUri: `${envConfigs.local.remoteModule.origin}/${placementId}`,
							externals: EXTERNALS,
						},
					],
				]),
			],
			[
				'test',
				new Map([
					[
						VARIANT_LATEST,
						{
							moduleName,
							moduleUri: `${envConfigs.test.remoteModule.origin}/${placementId}`,
							externals: EXTERNALS,
						},
					],
				]),
			],
			[
				'staging',
				new Map([
					[
						VARIANT_LATEST,
						{
							moduleName,
							moduleUri: `${envConfigs.staging.remoteModule.origin}/${placementId}`,
							externals: EXTERNALS,
						},
					],
				]),
			],
			[
				'production',
				new Map([
					[
						VARIANT_LATEST,
						{
							moduleName,
							moduleUri: `${envConfigs.production.remoteModule.origin}/${placementId}`,
							externals: EXTERNALS,
						},
					],
				]),
			],
		]);
	}

	get dimensions(): AtlaskitPluginDimensions {
		return this._dimensions;
	}

	get manifestMap(): ManifestMap {
		return this._manifestMap;
	}

	get bifrostOverrideKey() {
		return `${this.placementId}-bifrost-override`;
	}

	public moduleUriMapper = memoizeOne<ModuleUriMapper>(
		(uri, variant = VARIANT_LATEST, computeHash) => {
			// The goal here is to take a version string of the form "18.1.2"
			// and get only the major version part.
			// eslint-disable-next-line @typescript-eslint/no-magic-numbers
			const reactMajorVersion = REACT_VERSION.split('.')[0] as string;

			// We want to hash the major version of React installed, alongside the chosen variant
			const hashedDependencies = ['react', 'react-dom'];
			const hashedValues = hashedDependencies.reduce<DependencyMap[]>(
				(mappings, dependency) =>
					mappings.concat([{ name: dependency, version: reactMajorVersion }]),
				[],
			);

			const hash = computeHash(variant, hashedValues);

			if (process.env['NODE_ENV'] === 'development') {
				// eslint-disable-next-line no-console
				console.debug(
					`[@post-office/remote-module-loader-plugin]: Computed hash for plugin [${
						this.constructor.name
					}] with variant [${variant}] and values ${JSON.stringify(hashedValues)}.`,
				);
			}

			return `${uri}.${hash}.js`;
		},
	);
}

const MODULE_NAME = '@atlassian/post-office-placement-remote';

export type Externals =
	| ExternalModuleName
	| '@atlassiansox/cross-flow-support'
	| '@atlaskit/react-ufo';

const EXTERNALS: RemoteModuleConfig<Externals>['externals'] = [
	{ name: ExternalModuleName.React },
	{ name: ExternalModuleName.ReactDOM },
	{ name: ExternalModuleName.ReactIntlNext },
	{ name: ExternalModuleName.EmotionReact },
	{ name: ExternalModuleName.FeatureGateJsClientPublic },
	{ name: ExternalModuleName.AnalyticsContext },
	{
		module: '@atlassiansox/cross-flow-support',
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		import: require('@atlassiansox/cross-flow-support'),
		isDefault: false,
	},
	{
		module: '@atlaskit/react-ufo',
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		import: require('@atlaskit/react-ufo'),
		isDefault: true,
	},
	// Long term we believe that we will want to include additional
	// dependencies to this list (such as heavy weight Atlaskit
	// components) but we want to wait until we know which components
	// are worth excluding.
];

export default RemotePostOfficePlugin;
